import { AfterViewInit, Component, ComponentRef, OnDestroy, OnInit, ViewChild, ViewContainerRef, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { PersonLoginService } from '../services/person-login-service';
import { StringUtils } from '../utils/string-utils';
import { Router } from '@angular/router';
import { EntityService } from '../services/entity-service';
import { Entity } from '../entity/entity';
import { EntityLineHeader } from '../entity/entity-line-header';
import { ProductModel } from './product-model';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { OrganizationService } from '../services/organization-service';
import { ConfigurationService } from './configuration.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AddProductDialogComponent } from './add-product-dialog/add-product-dialog.component';
import { ConfigurationSaveInformation } from '../model/configuration-save-information';
import { EntityLineService } from '../services/entity-line-service';
import { MultiplierService } from '../services/multiplier.service';
import { EntityKeyUtils } from '../utils/entity-key-utils';
import { ProductModelFactory } from './product-model-factory';
import { MassUpdateDialogComponent } from './mass-update-dialog/mass-update-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MassUpdate } from '../model/mass-update';
import { LeadTimeService } from '../services/lead-time.service';
import { IecLeadTime } from '../model/iec-lead-time';
import { EntityLine } from '../entity/entity-line';
import { TagInformation } from '../model/tag-information';
import { TagUtils } from '../utils/tag-utils';
import { LineResequenceInput } from '../model/line-resequence-input';
import { EntityLineUtils } from '../utils/entity-line-utils';
import { EntityUtils } from '../utils/entity-utils';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { DialogService } from '../services/dialog.service';
import { OrganizationPricelistService } from '../services/organization-pricelist.service';

@Component({
  selector: 'app-configurator',
  templateUrl: './configurator.component.html',
  styleUrls: ['./configurator.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, MatTooltip, CdkDropList, CdkDrag, MatProgressSpinner],
})
export class ConfiguratorComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly router = inject(Router);
  private readonly entityService = inject(EntityService);
  private readonly entityLineService = inject(EntityLineService);
  private readonly organizationService = inject(OrganizationService);
  private readonly configurationService = inject(ConfigurationService);
  private readonly multiplierService = inject(MultiplierService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly leadTimeService = inject(LeadTimeService);
  private readonly dialogService = inject(DialogService);
  private readonly organizationPricelistService = inject(OrganizationPricelistService);
  readonly entityId = input.required<string>();
  readonly entityRevision = input.required<string>();
  readonly entityType = input.required<string>();
  private readonly entity = signal<Entity>(undefined!);
  private loadedLines: Map<string, ComponentRef<ProductModel>> = new Map<string, ComponentRef<ProductModel>>();
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly entityLines = signal<EntityLineHeader[]>(undefined!);
  readonly updatedLines = signal<string[]>([]);
  readonly selectedLineId = signal<string>(undefined!);
  @ViewChild('modelDetail', { read: ViewContainerRef }) modelDetail!: ViewContainerRef;
  lineChangedSubscription!: Subscription;
  readonly entityLeadTime = signal<IecLeadTime>(undefined!);
  readonly slowestLeadTime = signal<IecLeadTime>(undefined!);

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.resetStatus();
      this.configurationService.isBusy.set(false);
      this.personLoginService.getCachedLogin();
      this.initialize();
    }
  }

  ngAfterViewInit() {
    this.lineChangedSubscription = this.configurationService.lineChanged().subscribe((lineId) => {
      if (!this.updatedLines()) {
        this.updatedLines.set([]);
      }
      if (this.updatedLines().indexOf(lineId) < 0) {
        this.updatedLines.update(current => [...current, lineId]);
      }
    });
  }

  ngOnDestroy() {
    this.modelDetail.clear();
    this.lineChangedSubscription.unsubscribe();
  }

  private backToMain(): void {
    this.router.navigate(['main']);
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
    this.configurationService.isBusy.set(false);
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
  }

  backToEntity(): void {
    let response = true;
    if (this.updatedLines() && this.updatedLines().length > 0) {
      response = confirm('You have unsaved changes. Do you want to cancel changes in progress?');
    }
    if (response) {
      const pageName = this.entity().id.entityName === 'QUOTE' ? 'quotes' : 'orders';
      this.router.navigate([pageName, this.entity().id.entityId, this.entity().id.entityRevision]);
    }
  }

  loadLineDetail(line: EntityLineHeader, isNew = false): void {
    if (!this.selectedLineId() || line.lineId !== this.selectedLineId()) {
      this.configurationService.isBusy.set(true);
      this.errorMessage.set(undefined!);
      this.isBusy.set(true);
      this.modelDetail.detach();
      let componentRef: ComponentRef<ProductModel>;
      if (this.loadedLines.has(line.lineId)) {
        componentRef = this.loadedLines.get(line.lineId)!;
        this.modelDetail.insert(componentRef.hostView);
        this.configurationService.isBusy.set(false);
        this.isBusy.set(false);
      } else {
        componentRef = this.modelDetail.createComponent<ProductModel>(ProductModelFactory.buildComponent(line.product));
        this.modelDetail.detach();
        componentRef.instance.entityLineHeader = line;
        componentRef.instance.entity = this.entity();
        componentRef.instance.entityLeadTime = this.entityLeadTime();
        componentRef.instance.slowestLeadTime = this.slowestLeadTime();
        componentRef.instance.initialize(isNew).subscribe({
          next: () => {
            this.loadedLines.set(line.lineId, componentRef);
            this.modelDetail.insert(componentRef.hostView);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      }
      this.selectedLineId.set(line.lineId);
    }
  }

  lineItemDropped(event: CdkDragDrop<EntityLineHeader[]>): void {
    if (!this.dropListDisabled()) {
      if (event.previousIndex !== event.currentIndex) {
        this.isBusy.set(true);
        const input = new LineResequenceInput();
        input.entityKey = EntityKeyUtils.convertToFullEntityKey(this.entity().id);
        input.newIndex = event.currentIndex;
        input.oldIndex = event.previousIndex;
        this.clearLoadedInformation();
        this.configurationService.resequenceLines(input).subscribe({
          next: () => {
            this.entityService.getEntityByKey(this.entity().id).subscribe({
              next: (entity) => {
                this.entityLines.set(entity.lines.filter((line) => EntityUtils.isConfigurableLine(entity, line)));
                this.isBusy.set(false);
              },
              error: (error) => this.handleError(error),
            });
          },
          error: (error) => this.handleError(error),
        });
      }
    } else {
      this.snackBar.open('Please save all current changes before reordering lines', 'Close', {
        duration: 15000,
      });
    }
  }

  private clearLoadedInformation(): void {
    this.selectedLineId.set(null!);
    this.loadedLines = new Map();
    this.modelDetail.clear();
    this.updatedLines.set([]);
  }

  isLineSelected(line: EntityLineHeader): boolean {
    return !!(this.selectedLineId() && line.lineId === this.selectedLineId());
  }

  isLineUpdated(line: EntityLineHeader): boolean {
    return this.updatedLines().filter((updated) => updated === line.lineId).length === 1;
  }

  lineTagsValid(line: EntityLineHeader): boolean {
    if (this.loadedLines.has(line.lineId)) {
      const model: ProductModel = this.loadedLines.get(line.lineId)!.instance;
      if (model && model.taggingDetails() && model.entityLine()) {
        return model.entityLine().quantityExtended === model.taggingDetails().length;
      } else {
        return true;
      }
    } else {
      return true;
    }
  }

  saveUnit(): void {
    this.saveLine(this.selectedLineId(), true);
  }

  private saveLine(lineId: string, removeFromList: boolean): void {
    if (this.isLineLoaded(lineId)) {
      this.resetStatus();
      const componentRef = this.loadedLines.get(lineId);
      const productModel = componentRef!.instance;
      this.synchronizeTaggingDetailsAndQuantity(productModel.entityLine(), productModel.taggingDetails());
      const quantity = productModel.entityLine().quantityExtended;
      const tagCount = productModel.taggingDetails().length;
      if (quantity !== tagCount) {
        alert('You cannot save a quantity of ' + quantity + ' with ' + tagCount + ' tags.' + '  Please remove the unwanted tags from line ' + lineId + ' before saving');
      } else {
        productModel.saveUnit();
        if (removeFromList) {
          this.updatedLines.update(current => { const next = [...current]; next.splice(this.updatedLines().indexOf(lineId), 1); return next; });
        }
      }
      this.isBusy.set(false);
    }
  }

  private synchronizeTaggingDetailsAndQuantity(line: EntityLine, tags: TagInformation[]) {
    const quantity = line.quantityExtended;
    const tagCount = tags.length;
    if (tagCount < quantity) {
      let lastSequence = tags.map((tag) => tag.sequence).sort((first, second) => second - first)[0];
      for (let i = 0; i < quantity - tagCount; i++) {
        let tagInformation = new TagInformation();
        if (TagUtils.allTaggingIsEqual(tags)) {
          tagInformation = TagUtils.copyTagInformation(tags[0]);
        }
        tagInformation.sequence = ++lastSequence;
        tags.push(tagInformation);
      }
    } else if (tagCount > quantity) {
      if (TagUtils.allTaggingIsEqual(tags)) {
        tags.splice(0, tagCount - quantity);
      }
    }
  }

  private isLineLoaded(lineId: string): boolean {
    return !!(lineId && lineId.trim().length > 0 && this.loadedLines.has(lineId));
  }

  addUnit(): void {
    const productDialog = this.dialog.open(AddProductDialogComponent, {
      height: '520px',
      width: '680px',
      disableClose: true,
      data: {
        entity: this.entity(),
      },
    });
    productDialog.afterClosed().subscribe({
      next: (productId) => {
        if (productId && productId.trim().length > 0) {
          this.resetStatus();
          const information = new ConfigurationSaveInformation();
          information.entityKey = EntityKeyUtils.convertToFullEntityKey(this.entity().id);
          information.prnField1 = this.entity().prnField1;
          this.configurationService.isBusy.set(true);
          this.configurationService.addProduct(productId, information).subscribe({
            next: (entityLine) => {
              this.entityLines.update(current => [...current, entityLine]);
              this.loadLineDetail(entityLine, true);
            },
            error: (error) => this.handleError(error),
          });
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  deleteLine(): void {
    if (this.isLineLoaded(this.selectedLineId())) {
      const lines = this.entityLines().filter((line) => line.lineId === this.selectedLineId());
      this.resetStatus();
      this.entityLineService.deleteLines(lines).subscribe({
        next: () => {
          const viewIndex = this.modelDetail.indexOf(this.loadedLines.get(this.selectedLineId())!.hostView);
          if (viewIndex >= 0) {
            this.modelDetail.remove(viewIndex);
          }
          this.loadedLines.delete(this.selectedLineId());
          const updatedIndex = this.updatedLines().indexOf(this.selectedLineId());
          if (updatedIndex >= 0) {
            this.updatedLines.update(current => { const next = [...current]; next.splice(updatedIndex, 1); return next; });
          }
          const lineIndex = this.entityLines().indexOf(lines[0]);
          if (lineIndex >= 0) {
            this.entityLines.update(current => { const next = [...current]; next.splice(lineIndex, 1); return next; });
          }
          this.selectedLineId.set(null!);
          this.multiplierService.runMultiplier(EntityKeyUtils.convertToFullEntityKey(this.entity().id)).subscribe({
            next: () => (this.isBusy.set(false)),
            error: (error) => this.handleError(error),
          });
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  copyLine(): void {
    if (this.selectedLineId() && this.selectedLineId().trim().length > 0) {
      this.resetStatus();
      this.errorMessage.set(null!);
      const information = new ConfigurationSaveInformation();
      information.entityKey = EntityKeyUtils.convertToFullEntityKey(this.entity().id);
      information.lineId = this.selectedLineId();
      this.configurationService.isBusy.set(true);
      this.isBusy.set(true);
      this.configurationService.copyConfiguration(information).subscribe({
        next: (line) => {
          this.entityLines.update(current => [...current, line]);
          this.loadLineDetail(line, false);
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  saveAll(): void {
    if (this.updatedLines() && this.updatedLines().length > 0) {
      this.resetStatus();
      this.isBusy.set(true);
      this.configurationService.isBusy.set(true);
      this.saveAllLines()
        .then(() => {
          this.isBusy.set(false);
          this.configurationService.isBusy.set(false);
        })
        .catch((error: any) => this.handleError(error));
    }
  }

  private async getSaveAllLineInformation(): Promise<ConfigurationSaveInformation[]> {
    const configurations: ConfigurationSaveInformation[] = [];
    for (const line of this.updatedLines()) {
      if (this.isLineLoaded(line)) {
        const componentRef = this.loadedLines.get(line);
        const productModel = componentRef!.instance;
        this.synchronizeTaggingDetailsAndQuantity(productModel.entityLine(), productModel.taggingDetails());
        const quantity = productModel.entityLine().quantityExtended;
        const tagCount = productModel.taggingDetails().length;
        if (quantity !== tagCount) {
          this.errorMessage.set('You cannot save a quantity of ' + quantity + ' with ' + tagCount + ' tags.' + '  Please remove the unwanted tags from line ' + line + ' before saving');
          this.isBusy.set(false);
          return undefined!;
        }
        await productModel.updateEnginePerformanceValues();
        configurations.push(productModel.getSaveInformation());
      }
    }
    return configurations;
  }

  private async saveAllLines(): Promise<void> {
    const configurations = await this.getSaveAllLineInformation();
    const updatedLines: EntityLine[] = await firstValueFrom<EntityLine[]>(this.configurationService.saveConfigurations(configurations));
    for (const updatedLine of updatedLines) {
      const headerFilter = this.entityLines().filter((entityLine) => entityLine.lineId === updatedLine.lineId);
      if (headerFilter && headerFilter.length === 1) {
        const header = headerFilter[0];
        header.description = updatedLine.lineDescription;
        header.configurationComplete = updatedLine.configurationComplete;
        header.finalExtendedPrice = updatedLine.finalExtendedPrice;
        header.finalUnitPrice = updatedLine.finalUnitPrice;
        header.prn = updatedLine.internalNotes;
        header.quantity = updatedLine.quantityExtended;
        this.updatedLines.update(current => { const next = [...current]; next.splice(this.updatedLines().indexOf(updatedLine.lineId), 1); return next; });
      }
    }
    this.clearLoadedInformation();
  }

  addUnitDisabled(): boolean {
    return this.isBusy() || this.configurationService.isBusy() || this.isApprovedOrFrozen();
  }

  private isApprovedOrFrozen(): boolean {
    return !!(this.entity() && this.entity().approvalStatus && (this.entity().approvalStatus.indexOf('Approved') >= 0 || this.entity().frozen));
  }

  deleteUnitDisabled(): boolean {
    return this.isBusy() || this.configurationService.isBusy() || !this.selectedLineId() || this.selectedLineId().trim().length === 0 || this.isApprovedOrFrozen();
  }

  copyLineDisabled(): boolean {
    return (
      this.isBusy() ||
      this.configurationService.isBusy() ||
      !this.selectedLineId() ||
      this.selectedLineId().trim().length === 0 ||
      this.isApprovedOrFrozen() ||
      this.isDiscontinuedProduct()
    );
  }

  private isDiscontinuedProduct(): boolean {
    if (this.entity() && this.entityLines() && this.selectedLineId()) {
      const filtered = this.entityLines().filter((line) => line.lineId === this.selectedLineId());
      if (filtered.length === 1) {
        return EntityLineUtils.hasDiscontinuedProduct(filtered[0]);
      }
    }
    return false;
  }

  saveUnitDisabled(): boolean {
    let isDeprecated = false;
    if (this.selectedLineId()) {
      const lines = this.entityLines().filter((line) => line.lineId === this.selectedLineId());
      if (lines.length === 1) {
        isDeprecated = EntityLineUtils.hasDiscontinuedProduct(lines[0]);
      }
    }
    return this.isBusy() || this.configurationService.isBusy() || !this.selectedLineId() || this.selectedLineId().trim().length === 0 || isDeprecated;
  }

  saveAllDisabled(): boolean {
    return this.isBusy() || this.configurationService.isBusy() || !this.updatedLines() || this.updatedLines().length === 0;
  }

  massUpdateDisabled(): boolean {
    return (
      this.isBusy() ||
      this.configurationService.isBusy() ||
      (this.updatedLines() && this.updatedLines().length > 0) ||
      this.isApprovedOrFrozen() ||
      this.getMassUpdateEligibleLines().length === 0
    );
  }

  private dropListDisabled(): boolean {
    return (this.updatedLines() && this.updatedLines().length > 0) || this.isBusy() || this.configurationService.isBusy();
  }

  private getMassUpdateEligibleLines(): EntityLineHeader[] {
    if ((this.entityLines()?.length || 0) > 0) {
      return this.entityLines().filter((line) => !EntityLineUtils.hasDiscontinuedProduct(line));
    }
    return [];
  }

  massUpdate(): void {
    const eligibleLines = this.getMassUpdateEligibleLines();
    if (eligibleLines && eligibleLines.length > 0) {
      const massUpdateDialog = this.dialog.open(MassUpdateDialogComponent, {
        height: '768px',
        width: '1320px',
        disableClose: true,
        data: { lines: eligibleLines, leadTimeCode: this.entity().leadTimeCode },
      });
      massUpdateDialog.afterClosed().subscribe({
        next: (massUpdate: MassUpdate) => {
          if (massUpdate) {
            this.modelDetail.detach();
            this.isBusy.set(true);
            this.massUpdateLineData(massUpdate)
              .then(() => {
                this.isBusy.set(false);
                this.configurationService.isBusy.set(false);
              })
              .catch((error: any) => this.handleError(error));
          }
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  private async massUpdateLineData(massUpdate: MassUpdate): Promise<void> {
    for (const header of massUpdate.lines) {
      let componentRef: ComponentRef<ProductModel>;
      if (this.loadedLines.has(header.lineId)) {
        componentRef = this.loadedLines.get(header.lineId)!;
        await componentRef.instance.applyMassUpdate(massUpdate.updatedEntities);
      } else {
        componentRef = this.modelDetail.createComponent<ProductModel>(ProductModelFactory.buildComponent(header.product));
        this.modelDetail.detach();
        componentRef.instance.entityLineHeader = header;
        componentRef.instance.entity = this.entity();
        componentRef.instance.entityLeadTime = this.entityLeadTime();
        componentRef.instance.slowestLeadTime = this.slowestLeadTime();
        this.isBusy.set(true);
        await firstValueFrom<string>(componentRef.instance.initialize(false));
        this.loadedLines.set(header.lineId, componentRef);
        await componentRef.instance.applyMassUpdate(massUpdate.updatedEntities);
      }
    }
  }

  openPerformanceDefaultsDialog(): void {
    this.dialogService.showEntityPerformanceDefaultsDialog(this.entity().id.entityId, this.entity().id.entityRevision);
  }

  private initialize(): void {
    const entityType: string = this.entityType();
    const entityId: string = this.entityId();
    const entityRevision: string = this.entityRevision();
    if (!entityType || entityType.trim().length === 0 || !entityId || entityId.trim().length === 0 || !entityRevision || isNaN(+entityRevision)) {
      this.backToMain();
    } else {
      this.entityService.getEntity(entityType, entityId, +entityRevision).subscribe({
        next: (entity) => {
          this.entity.set(entity);
          this.leadTimeService.getLeadTimeByProgram(this.entity().leadTimeCode).subscribe({
            next: (leadTime) => {
              this.entityLeadTime.set(leadTime);
              this.leadTimeService.getSlowestLeadTime(entity.brandID).subscribe({
                next: (slowest) => {
                  this.slowestLeadTime.set(slowest);
                  this.entityLines.set(entity.lines.filter((line) => EntityUtils.isConfigurableLine(entity, line) && line.product.toUpperCase().indexOf('SUREFLOW') < 0));
                  this.organizationService.getOrganization(entity.repOfficeId).subscribe({
                    next: (organization) => {
                      this.entity().prnField1 = organization.prnCode;
                      this.organizationPricelistService.getOrgUnitXPriceList(entity.repOfficeId, entity.projectProductType, entity.brandID).subscribe({
                        next: (organizationXPriceList) => {
                          this.entity().priceListId = organizationXPriceList.priceList.priceListId;
                        },
                        error: (error) => this.handleError(error),
                      });
                      this.isBusy.set(false);
                    },
                    error: (error) => this.handleError(error),
                  });
                },
                error: (error) => this.handleError(error),
              });
            },
            error: (error) => this.handleError(error),
          });
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }
}
